import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { HttpStatusCode, isAxiosError } from "axios";
import { useApiClient } from "@/hooks/useApiClient";
import { isIpNotAllowedError } from "@/utils/ipRestriction";
import {
  getConnection,
  SANSAN_CONNECTION_ENDPOINT,
} from "@/features/sansanIntegration/api/getConnection";
import { deleteConnection } from "@/features/sansanIntegration/api/deleteConnection";
import { postConnection } from "@/features/sansanIntegration/api/postConnection";
import { deriveConnectionStatus } from "@/features/sansanIntegration/utils/deriveConnectionStatus";
import { extractErrorDetail } from "@/features/sansanIntegration/utils/extractErrorDetail";
import {
  type SansanConnectionResponse,
  type SansanConnectionStatus,
} from "@/features/sansanIntegration/types";

export interface UseSansanConnectionOptions {
  /** `false`の場合はフェッチしない（LaunchDarklyフラグが無効な画面での無駄なリクエスト抑止用）。 */
  enabled?: boolean;
}

export interface UseSansanConnectionResult {
  /** 未取得は`undefined`、未連携は`null` */
  connection: SansanConnectionResponse | null | undefined;
  connectionStatus: SansanConnectionStatus;
  /** APIキーを登録する。保存前にバックエンドが検証し、無効なキーは保存されない。 */
  registerApiKey: (apiKey: string) => Promise<void>;
  isRegistering: boolean;
  disconnect: () => Promise<void>;
  isDisconnecting: boolean;
}

/**
 * 5xx・レスポンスのないエラー・想定外の例外（axios以外）をSentryに送る。
 * 4xx は利用者の操作やデータが原因のため送らない（参謀くん連携の useSendToSamboKun と同じ方針）。
 * レスポンスのないエラー（バックエンドの停止・タイムアウト・通信断）は Django まで届かずバックエンド側に残らないため、FEから送る。
 *
 * SendToTrackingService はレスポンスの config（APIキーの平文を含むリクエストボディ・認証ヘッダー）を
 * そのまま送るため使わず、メソッド・URL・ステータスだけを送る。
 */
const reportToSentry = (error: unknown): void => {
  if (!isAxiosError(error)) {
    Sentry.captureException(error);
    return;
  }
  const status = error.response?.status;
  if (status !== undefined && status < HttpStatusCode.InternalServerError) {
    return;
  }
  const method = error.config?.method ?? "";
  const url = error.config?.url ?? "";
  Sentry.captureException(error, {
    fingerprint: ["{{ default }}", url, String(status), method],
    contexts: { request: { method, url, status: status ?? null } },
  });
};

/**
 * グローバルの SWRConfig.onError（App.tsx）の「予期しないエラー」トーストを抑止する。
 * 登録・解除の失敗は呼び出し側の catch でトーストとSentry送信を行うため、重ねて出さない。
 */
const suppressGlobalErrorToast = (): void => {
  // 何もしない（呼び出し側の catch で処理済み）
};

const hasStatus = (error: unknown, status: HttpStatusCode): boolean =>
  isAxiosError(error) && error.response?.status === status;

/**
 * App.tsx のグローバル onError と同じく、トーストを出さないエラーか。
 * 401（認証切れ）は再認証・ログイン画面への遷移を別の仕組みで行い、許可IP外は専用画面へ遷移済みのため。
 */
const isHandledElsewhere = (error: unknown): boolean =>
  hasStatus(error, HttpStatusCode.Unauthorized) || isIpNotAllowedError(error);

/**
 * Sansan連携の状態の取得と、APIキーの登録・連携解除の操作をまとめたhook。
 * 差し替えの操作はない。APIキーを替える場合は、連携を解除してから登録し直す。
 */
export const useSansanConnection = (
  options: UseSansanConnectionOptions = {}
): UseSansanConnectionResult => {
  const { enabled = true } = options;
  const { apiClient } = useApiClient();

  const { data, mutate } = useSWR<SansanConnectionResponse | null>(
    enabled ? SANSAN_CONNECTION_ENDPOINT : null,
    async () => {
      try {
        return await getConnection(apiClient);
      } catch (error) {
        // 汎用のトーストは App.tsx のグローバル onError が出すため、ここでは Sentry への送信だけ行う
        reportToSentry(error);
        throw error;
      }
    },
    { revalidateOnFocus: false }
  );

  const registerMutation = useSWRMutation(
    SANSAN_CONNECTION_ENDPOINT,
    async (_key: string, { arg: apiKey }: { arg: string }) =>
      await postConnection(apiClient, apiKey),
    { revalidate: false, onError: suppressGlobalErrorToast }
  );

  const disconnectMutation = useSWRMutation(
    SANSAN_CONNECTION_ENDPOINT,
    async () => {
      await deleteConnection(apiClient);
    },
    { revalidate: false, onError: suppressGlobalErrorToast }
  );

  const registerApiKey = async (apiKey: string): Promise<void> => {
    try {
      const registered = await registerMutation.trigger(apiKey);
      await mutate(registered, { revalidate: false });
      toast.success("Sansan APIキーを登録しました");
    } catch (error) {
      reportToSentry(error);
      if (isHandledElsewhere(error)) {
        return;
      }
      // 別のタブなどで登録済みだった場合（409）は、最新の状態を取り直して表示する。
      // 取り直しに失敗した場合（mutateは例外を投げない）は、バックエンドのメッセージ（再読み込みの案内）を表示する
      if (hasStatus(error, HttpStatusCode.Conflict)) {
        const latest = await mutate();
        if (latest) {
          toast.info("すでに連携されていたため、最新の状態を表示しました");
          return;
        }
      }
      toast.error(extractErrorDetail(error, "APIキーの登録に失敗しました"));
    }
  };

  const disconnect = async (): Promise<void> => {
    try {
      await disconnectMutation.trigger();
      // 解除済みと分かっているため、取り直しを待たずに未連携として表示する
      await mutate(null, { revalidate: false });
      toast.success("Sansanとの連携を解除しました");
    } catch (error) {
      reportToSentry(error);
      if (isHandledElsewhere(error)) {
        return;
      }
      // 別のタブなどで解除済みだった場合（404）は、未連携として表示する
      if (hasStatus(error, HttpStatusCode.NotFound)) {
        await mutate(null, { revalidate: false });
        toast.info("すでに連携が解除されていたため、最新の状態を表示しました");
        return;
      }
      toast.error(extractErrorDetail(error, "連携の解除に失敗しました"));
    }
  };

  return {
    connection: data,
    connectionStatus: deriveConnectionStatus(data),
    registerApiKey,
    isRegistering: registerMutation.isMutating,
    disconnect,
    isDisconnecting: disconnectMutation.isMutating,
  };
};
